/**
 * The Job bar's model (JOBS-UX-SPEC 1.1 to 1.5, 1.8; lucid 007).
 *
 * Pure: what the bar shows is decided here. JobBar.tsx only draws it. The Job
 * members carry slice A's fields (spec 6.1): splitArea, importedRev and the
 * last send (lastSentRev, lastSentDisplayQuoteNo, lastSentAt), computed on the
 * server because the Job's revisions list cannot see a send made before the
 * quote joined a Job.
 *
 * The bar is ONE row by rule (D1): the current quote's card and at most two
 * others. The rest sit behind "+N more", which opens Job history.
 */

import type { QuoteJobMember, QuoteJobView } from './quote-jobs-client';
import { quoteKind, showsKindTag, type QuoteKind } from './quote-kind';
import { formatDayMonth, formatFullDate } from './job-dates';

export type JobBarTone = 'finish' | 'send' | 'waiting' | 'won' | 'lost';

/** A Job member as slice A returns it (spec 6.1). The new fields are optional so an old backend still reads. */
export type JobBarMember = QuoteJobMember & {
    splitArea?: string | null;
    importedRev?: number | string | null;
    lastSentRev?: number | string | null;
    lastSentDisplayQuoteNo?: string | null;
    lastSentAt?: string | null;
};

export interface ClientHas { rev: number; label: string; sentAt: string | null; isThisRev: boolean }

export interface JobBarCard {
    id: string;
    label: string;
    title: string;
    quoteRev: number;
    kind: QuoteKind;
    showKindTag: boolean;
    splitArea: string | null;
    clientHas: ClientHas | null;
    statusText: string;
    statusTitle: string;
    statusTone: JobBarTone;
    /** null on the current card: the quote below shows its total. */
    totalIncGst: number | null;
    isCurrent: boolean;
}

export interface JobBarModel {
    hasJob: boolean;
    title: string;
    quoteRequestNo: string | null;
    strataPlan: string | null;
    quoteCount: number;
    /** Visible cards, in creation order (1.4). */
    cards: JobBarCard[];
    /** Behind "+N more". */
    hiddenIds: string[];
    siteVisit: { requestedAt: string; text: string; title: string } | null;
    decidedNote: string | null;
    phoneVisible: boolean;
}

type FormatLabel = (input: { qmQuoteNo?: string | null; quoteRev?: number | null; sourceQuoteRef?: string | null }) => string;

// ── References ──────────────────────────────────────────────────────────────

const REFERENCE_KEY: Record<'QR' | 'SP', RegExp> = {
    SP: /^(?:strata\s+plan|s\.?\s?p\.?)(?:\s*(?:no\.?|number|#|:|-)\s*|\s*)/i,
    QR: /^(?:quote\s+request|q\.?\s?r\.?)(?:\s*(?:no\.?|number|#|:|-)\s*|\s*)/i,
};

/**
 * The value of a QR or SP reference without the key the bar already prints.
 * "SP 80123" and "Strata Plan 80123" become "80123"; "QR# 4411" becomes
 * "4411". A value whose remainder does not start with a digit ("Sparks
 * House") is returned as typed.
 */
export function stripReferenceKey(value: string | null | undefined, key: 'QR' | 'SP'): string | null {
    const text = (typeof value === 'string' ? value : value == null ? '' : String(value)).trim();
    if (!text) return null;
    const match = text.match(REFERENCE_KEY[key]);
    if (!match) return text;
    const rest = text.slice(match[0].length).trim();
    if (!rest || !/^[0-9]/.test(rest)) return text;
    return rest;
}

// ── When the bar shows (1.1) ────────────────────────────────────────────────

export function jobBarVisible(input: { hasJob: boolean; quoteRev: number; keptRevisionCount: number; canRevise: boolean }): boolean {
    return Boolean(input.hasJob || (Number(input.quoteRev) || 0) > 0 || (Number(input.keptRevisionCount) || 0) > 0 || input.canRevise);
}

// ── Cards ───────────────────────────────────────────────────────────────────

function intOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

function textOrNull(value: unknown): string | null {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function laneOf(member: Pick<QuoteJobMember, 'pdfGenerated' | 'currentStage'>): { word: string; tone: 'send' | 'finish' } {
    if (member.pdfGenerated || Number(member.currentStage) >= 5) return { word: 'Ready to send', tone: 'send' };
    return { word: 'In progress', tone: 'finish' };
}

function clientHasFor(member: JobBarMember, rev: number, label: string): ClientHas | null {
    if (member.emailSent) return { rev, label, sentAt: member.emailSentAt ?? null, isThisRev: true };
    const sentRev = intOrNull(member.lastSentRev);
    const sentLabel = textOrNull(member.lastSentDisplayQuoteNo);
    if (sentRev === null || sentRev > rev) return null;
    const sentAt = textOrNull(member.lastSentAt);
    if (sentRev === rev) return { rev, label, sentAt, isThisRev: true };
    return { rev: sentRev, label: sentLabel || `Rev ${sentRev}`, sentAt, isThisRev: false };
}

function status(
    member: JobBarMember,
    label: string,
    clientHas: ClientHas | null,
    isCurrent: boolean,
): { text: string; title: string; tone: JobBarTone } {
    const name = label || 'This quote';
    const decided = String(member.quoteStatus || '').toLowerCase();
    if (decided === 'won') return { text: 'Won', title: `${name} was marked won.`, tone: 'won' };
    if (decided === 'lost') return { text: 'Lost', title: `${name} was marked lost.`, tone: 'lost' };
    if (clientHas?.isThisRev) {
        const day = formatDayMonth(clientHas.sentAt);
        const full = formatFullDate(clientHas.sentAt);
        return {
            text: day ? `Sent ${day}` : 'Sent',
            title: full ? `${name} was sent to the client on ${full}.` : `${name} was sent to the client.`,
            tone: 'waiting',
        };
    }
    const lane = laneOf(member);
    const laneSentence = lane.tone === 'send' ? `${name} is ready to send.` : `${name} is in progress.`;
    if (clientHas) {
        const day = formatDayMonth(clientHas.sentAt);
        const full = formatFullDate(clientHas.sentAt);
        return {
            text: isCurrent && day ? `Client has ${clientHas.label} (${day})` : `Client has ${clientHas.label}`,
            title: `${laneSentence} The client has ${clientHas.label}${full ? `, sent ${full}` : ''}.`,
            tone: lane.tone,
        };
    }
    return { text: lane.word, title: laneSentence, tone: lane.tone };
}

function card(
    member: JobBarMember,
    formatLabel: FormatLabel,
    quoteCount: number,
    isCurrent: boolean,
): JobBarCard {
    const quoteRev = intOrNull(member.quoteRev) ?? 0;
    const label = formatLabel({ qmQuoteNo: member.qmQuoteNo, quoteRev, sourceQuoteRef: member.sourceQuoteRef });
    const splitArea = textOrNull(member.splitArea);
    const kind = quoteKind({ quoteRev, splitArea, importedRev: intOrNull(member.importedRev) });
    const clientHas = clientHasFor(member, quoteRev, label);
    const s = status(member, label, clientHas, isCurrent);
    const total = typeof member.quoteTotal === 'number' && Number.isFinite(member.quoteTotal) ? member.quoteTotal : null;
    return {
        id: member.id,
        label,
        title: member.title || '',
        quoteRev,
        kind,
        showKindTag: showsKindTag(kind, quoteCount),
        splitArea,
        clientHas,
        statusText: s.text,
        statusTitle: s.title,
        statusTone: s.tone,
        totalIncGst: isCurrent ? null : total,
        isCurrent,
    };
}

/** The open quote as a member: the live Quote # and Rev over the member row, which may lag. */
function currentMember(
    base: JobBarMember | null,
    currentProjectId: string,
    summary: any,
    meta: any,
): JobBarMember {
    const s = summary || {};
    const from = base || ({} as Partial<JobBarMember>);
    const fd = s.formData || {};
    return {
        id: currentProjectId,
        title: String(from.title ?? s.title ?? ''),
        quoteNumber: from.quoteNumber ?? s.quoteNumber ?? fd.orderNumber ?? null,
        qmQuoteNo: meta.qmQuoteNo ?? from.qmQuoteNo ?? s.qmQuoteNo ?? null,
        quoteRev: intOrNull(meta.quoteRev) ?? intOrNull(from.quoteRev) ?? intOrNull(s.quoteRev) ?? 0,
        sourceQuoteRef: meta.sourceQuoteRef ?? from.sourceQuoteRef ?? s.sourceQuoteRef ?? null,
        sourceIssuer: from.sourceIssuer ?? s.sourceIssuer ?? null,
        status: from.status ?? s.status ?? null,
        quoteStatus: s.quoteStatus ?? s.quote_status ?? from.quoteStatus ?? null,
        pdfGenerated: typeof s.pdfGenerated === 'boolean' ? s.pdfGenerated : from.pdfGenerated === true,
        emailSent: typeof s.emailSent === 'boolean' ? s.emailSent : from.emailSent === true,
        emailSentAt: s.emailSentAt ?? from.emailSentAt ?? null,
        currentStage: s.currentStage ?? from.currentStage ?? null,
        quoteTotal: from.quoteTotal ?? null,
        createdAt: from.createdAt ?? s.createdAt ?? '',
        updatedAt: from.updatedAt ?? s.updatedAt ?? '',
        splitArea: from.splitArea ?? s.splitArea ?? null,
        importedRev: from.importedRev ?? s.importedRev ?? null,
        lastSentRev: from.lastSentRev ?? null,
        lastSentDisplayQuoteNo: from.lastSentDisplayQuoteNo ?? null,
        lastSentAt: from.lastSentAt ?? null,
    };
}

export function buildJobBarModel(input: {
    jobView: QuoteJobView | null;
    currentProjectId: string;
    summary: any;
    quoteData: any;
    formatLabel: FormatLabel;
    now?: Date;
}): JobBarModel {
    const meta = input.quoteData?.meta || {};
    const summary = input.summary || {};
    const members = (input.jobView?.quotes || []) as JobBarMember[];
    const hasJob = Boolean(input.jobView?.job);
    const inJob = members.find((m) => m.id === input.currentProjectId) || null;
    const current = currentMember(inJob, input.currentProjectId, summary, meta);
    // Creation order is the server's (listJobQuotes, created_at ascending). An
    // open quote the Job view does not list yet goes last.
    const ordered: JobBarMember[] = hasJob
        ? (inJob ? members.map((m) => (m.id === current.id ? current : m)) : [...members, current])
        : [current];
    const quoteCount = ordered.length;

    const siteVisitAt = textOrNull(summary.siteVisitRequestedAt);
    const decided = String(current.quoteStatus || '').toLowerCase();
    const decidedNote = decided === 'won'
        ? 'Won quotes are not revised. Extra work needs a new quote.'
        : decided === 'lost' ? 'Lost quotes are not revised.' : null;

    // 1.4: the current card and at most two others; one when anything else
    // competes for the row.
    const others = ordered.filter((m) => m.id !== current.id);
    const roomForTwo = !siteVisitAt && !decidedNote && quoteCount <= 3;
    const shownOthers = new Set(others.slice(0, roomForTwo ? 2 : 1).map((m) => m.id));
    const visible = ordered.filter((m) => m.id === current.id || shownOthers.has(m.id));
    const cards = visible.map((m) => card(m, input.formatLabel, quoteCount, m.id === current.id));
    const hiddenIds = others.filter((m) => !shownOthers.has(m.id)).map((m) => m.id);

    let siteVisit: JobBarModel['siteVisit'] = null;
    if (siteVisitAt) {
        const rev = intOrNull(current.quoteRev) ?? 0;
        const revName = rev > 0 ? `Rev ${rev}` : 'this quote';
        const day = formatDayMonth(siteVisitAt);
        const full = formatFullDate(siteVisitAt);
        siteVisit = {
            requestedAt: siteVisitAt,
            text: day ? `Site visit requested ${day}` : 'Site visit requested',
            title: `Requested with ${revName}${full ? ` on ${full}` : ''}. It clears when ${revName} is sent.`,
        };
    }

    const fd = summary.formData || {};
    const title = hasJob
        ? String(input.jobView?.job?.title || '').trim()
        : String(summary.title || current.title || '').trim();
    const qrRaw = hasJob ? input.jobView?.job?.quoteRequestNo : (summary.quoteNumber ?? fd.orderNumber);
    const spRaw = hasJob ? input.jobView?.job?.strataPlan : (fd.strataplan ?? fd.strataPlan ?? fd.strata_plan);

    return {
        hasJob,
        title,
        quoteRequestNo: stripReferenceKey(textOrNull(qrRaw), 'QR'),
        strataPlan: stripReferenceKey(textOrNull(spRaw), 'SP'),
        quoteCount,
        cards,
        hiddenIds,
        siteVisit,
        decidedNote,
        phoneVisible: quoteCount >= 2 || siteVisit !== null,
    };
}
